import type { AddressIntegrationData } from "../../../client";

export const KM_TO_MILES = 0.621371;

export const INTEGRATION_NOT_RUN = "N/A (not modeled)";
export const INTEGRATION_FOREIGN = "N/A (foreign)";
export const INTEGRATION_ZERO_TIV = "N/A ($0 TIV)";
export const INTEGRATION_NO_MATCH = "N/A (no match)";

/** Whether either Nearmap source (Betterview profile or AI material mix) has
 * data for the address. */
export function hasNearmapData(
  data: AddressIntegrationData | undefined
): boolean {
  return Boolean(data?.nearmap || data?.nearmap_ai);
}

/** Roof material: Nearmap AI's largest-share class, else Betterview's material
 * list. The share is kept out so equal materials group together in the pivot;
 * see nearmapRoofMaterialShare. */
export function nearmapRoofMaterial(
  data: AddressIntegrationData | undefined
): string | undefined {
  const top = data?.nearmap_ai?.roof_materials.shares[0];
  if (top) return top.material;
  const materials = data?.nearmap?.buildings?.[0]?.materials;
  return materials && materials.length > 0 ? materials.join(", ") : undefined;
}

/** Share of roof area held by the Nearmap AI material, e.g. "62%". Undefined
 * for Betterview-only profiles. */
export function nearmapRoofMaterialShare(
  data: AddressIntegrationData | undefined
): string | undefined {
  const top = data?.nearmap_ai?.roof_materials.shares[0];
  return top ? `${Math.round(top.ratio * 100)}%` : undefined;
}

const parseNumericField = (val: string | null | undefined): number | null => {
  if (val == null) return null;
  const n = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

// Blended ITV ($/sqft): total building value divided by total square footage
// across locations that report both a numeric building value and a non-zero
// square footage. Locations missing either are excluded from both sums, so the
// figure reflects only the locations it can actually be computed from. Returns
// null when no qualifying location exists.
export const blendedItvPerSqft = (
  addresses: {
    building_value?: string | null;
    square_footage?: string | null;
  }[]
): number | null => {
  let totalBuildingValue = 0;
  let totalSquareFootage = 0;
  for (const a of addresses) {
    const buildingValue = parseNumericField(a.building_value);
    const squareFootage = parseNumericField(a.square_footage);
    if (buildingValue == null || squareFootage == null || squareFootage <= 0) {
      continue;
    }
    totalBuildingValue += buildingValue;
    totalSquareFootage += squareFootage;
  }
  return totalSquareFootage === 0
    ? null
    : totalBuildingValue / totalSquareFootage;
};
